/**
===================================================
Module: frontend.src.components.Login.tsx
Description: Login, registration, and password reset component (Cognito)
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/config";
import NotificationSystem, { Notification } from "./NotificationSystem";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../styles/login.css";
import {
  isValidPhoneNumber,
  CountryCode,
} from "libphonenumber-js";
import logoImage from "../assets/logo.svg";

type AuthMode = "login" | "register" | "forgotPassword" | "resetPassword";

const Login: React.FC = () => {
  const { t } = useTranslation('auth');
  const selfSignUpEnabled = process.env.REACT_APP_ENABLE_SELF_SIGNUP !== 'false';
  const [mode, setMode] = useState<AuthMode>("login");

  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // State
  const [pendingEmail, setPendingEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [phoneError, setPhoneError] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("JP");

  const { login, register, confirmRegistration, forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Notification helpers
  const addNotification = (
    message: string,
    type: "success" | "error" | "info",
    duration = 5000,
  ) => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { id, message, type, duration }]);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // Handle URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get("message");
    const errorParam = urlParams.get("error");
    const urlMode = urlParams.get("mode");
    const urlCode = urlParams.get("code");
    const urlEmail = urlParams.get("email");

    if (message === "verified") {
      addNotification(t('login.emailVerified'), "success");
    } else if (message === "password_reset_success") {
      addNotification(t('login.passwordResetSuccess'), "success");
    } else if (errorParam) {
      addNotification(t('login.errorOccurred'), "error");
    }

    // Handle reset password link with pre-filled code and email
    if (urlMode === "resetPassword" && urlCode && urlEmail) {
      setMode("resetPassword");
      setPendingEmail(urlEmail);
      setVerificationCode(urlCode);
      addNotification(t('resetPassword.enterNewPassword'), "info");
    } else if (urlMode === "forgotPassword") {
      setMode("forgotPassword");
    }
    else if (urlMode === "register" && selfSignUpEnabled) {
      setMode("register");
    }
  }, [location]);

  // Phone validation
  const toE164 = (raw: string) => (raw?.startsWith("+") ? raw : `+${raw}`);

  const validatePhoneNumber = (raw: string): boolean => {
    if (!raw || raw.trim() === "") {
      setPhoneError("");
      return true; // Phone is optional
    }

    const e164 = toE164(raw);
    try {
      if (!isValidPhoneNumber(e164)) {
        setPhoneError(t('register.invalidPhone'));
        return false;
      }
      setPhoneError("");
      return true;
    } catch {
      setPhoneError(t('register.invalidPhone'));
      return false;
    }
  };

  const handlePhoneChange = (
    value: string,
    country: any,
  ) => {
    setPhoneNumber(value);
    const iso2 = (country?.countryCode ?? "jp").toUpperCase() as CountryCode;
    setSelectedCountry(iso2);

    const digits = value.replace(/\D/g, "");
    const dial = (country?.dialCode as string) || "";
    if (digits.length >= dial.length + 4) {
      validatePhoneNumber(value);
    } else {
      setPhoneError("");
    }
  };

  // Form validation
  const validateLoginForm = (): boolean => {
    if (!email.trim()) {
      addNotification(t('login.emailRequired'), "error");
      return false;
    }
    if (!password.trim()) {
      addNotification(t('login.passwordRequired'), "error");
      return false;
    }
    return true;
  };

  const validateRegistrationForm = (): boolean => {
    if (!email.trim()) {
      addNotification(t('register.emailRequired'), "error");
      return false;
    }
    if (!password.trim()) {
      addNotification(t('register.emailRequired'), "error");
      return false;
    }
    if (password.length < 8) {
      addNotification(t('register.passwordTooShort'), "error");
      return false;
    }
    if (password !== confirmPassword) {
      addNotification(t('register.passwordMismatch'), "error");
      return false;
    }
    if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
      return false;
    }
    return true;
  };

  // Form handlers
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLoginForm()) return;

    setIsLoading(true);
    try {
      await login({ email, password });
      navigate("/projects");
    } catch (error: any) {
      // Check if the error is about email verification (compare against translated string)
      const verifyEmailError = i18n.t('errors.pleaseVerifyEmail', { ns: 'auth' });
      if (error.message === verifyEmailError) {
        addNotification(t('login.emailNotVerified'), "error");
      } else {
        addNotification(error.message || t('login.loginError'), "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRegistrationForm()) return;

    setIsLoading(true);
    try {
      const result = await register({
        email,
        password,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        phoneNumber: phoneNumber || undefined,
      });

      // Registration successful - redirect to login with message to check email
      addNotification(t('register.checkEmailForVerification'), "success");
      resetForm();
      setMode("login");
    } catch (error: any) {
      addNotification(error.message || t('register.registrationError'), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      addNotification(t('forgotPassword.emailRequired'), "error");
      return;
    }

    setIsLoading(true);
    try {
      await forgotPassword(email);
      addNotification(t('forgotPassword.codeSent'), "success");
      // Stay on forgot password form - user will get email with reset link
      resetForm();
      setMode("login");
    } catch (error: any) {
      addNotification(error.message || t('forgotPassword.sendError'), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      addNotification(t('resetPassword.codeRequired'), "error");
      return;
    }
    if (!newPassword.trim()) {
      addNotification(t('resetPassword.passwordRequired'), "error");
      return;
    }
    if (newPassword.length < 8) {
      addNotification(t('resetPassword.passwordTooShort'), "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      addNotification(t('resetPassword.passwordMismatch'), "error");
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(pendingEmail, verificationCode, newPassword);
      addNotification(t('resetPassword.resetSuccess'), "success");
      resetForm();
      setMode("login");
    } catch (error: any) {
      addNotification(error.message || t('resetPassword.resetError'), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setVerificationCode("");
    setNewPassword("");
    setPendingEmail("");
    setPhoneError("");
  };

  const switchMode = (newMode: AuthMode) => {
    resetForm();
    setNotifications([]);
    setMode(newMode);
  };

  // Render helpers
  const renderLoginForm = () => (
    <>
      <div className="login-header">
        <div className="login-logo">
          <img src={logoImage} alt={t('logoAlt')} className="logo-image" />
        </div>
        <h1 className="login-title">{t('app.title')}</h1>
      </div>

      <form className="login-form" onSubmit={handleLoginSubmit}>
        <div className="form-group">
          <label className="form-label">{t('login.email')}</label>
          <input
            type="email"
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('login.emailPlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <div className="form-label-row">
            <label className="form-label">{t('login.password')}</label>
            <button
              type="button"
              className="forgot-password"
              onClick={() => switchMode("forgotPassword")}
              disabled={isLoading}
            >
              {t('login.forgotPassword')}
            </button>
          </div>
          <input
            type="password"
            className="form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('login.passwordPlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        <button type="submit" className="login-btn" disabled={isLoading || !email || !password}>
          {isLoading ? (
            <>
              <span className="spinner"></span>
              <span>{t('login.loggingIn')}</span>
            </>
          ) : (
            t('login.loginButton')
          )}
        </button>
      </form>

      {selfSignUpEnabled && (
        <div className="login-footer">
          <p className="login-footer-text">
            {t('login.noAccount')}{" "}
            <button className="signup-btn" onClick={() => switchMode("register")} disabled={isLoading}>
              {t('login.register')}
            </button>
          </p>
        </div>
      )}
    </>
  );

  const renderRegisterForm = () => (
    <>
      <div className="login-header">
        <div className="login-logo">
          <img src={logoImage} alt={t('logoAlt')} className="logo-image" />
        </div>
        <h1 className="login-title">{t('register.title')}</h1>
      </div>

      <form className="login-form" onSubmit={handleRegisterSubmit}>
        <div className="form-group">
          <label className="form-label">{t('register.email')}</label>
          <input
            type="email"
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('register.emailPlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('register.firstName')}</label>
            <input
              type="text"
              className="form-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('register.firstNamePlaceholder')}
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('register.lastName')}</label>
            <input
              type="text"
              className="form-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('register.lastNamePlaceholder')}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('register.phoneNumber')}</label>
          <PhoneInput
            country={"jp"}
            value={phoneNumber}
            onChange={handlePhoneChange}
            inputClass="phone-input-field"
            containerClass="phone-input-container"
            buttonClass="phone-input-button"
            enableSearch
            dropdownClass="phone-input-dropdown"
            searchPlaceholder={t('register.searchCountry')}
            inputProps={{
              name: "phoneNumber",
              placeholder: t('register.phonePlaceholder'),
              onBlur: () => validatePhoneNumber(phoneNumber),
            }}
          />
          {phoneError && <div className="input-error">{phoneError}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">{t('register.password')}</label>
          <input
            type="password"
            className="form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('register.passwordPlaceholder')}
            required
            minLength={8}
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('register.confirmPassword')}</label>
          <input
            type="password"
            className="form-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('register.confirmPasswordPlaceholder')}
            required
            minLength={8}
            disabled={isLoading}
          />
        </div>

        <button type="submit" className="login-btn" disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="spinner"></span>
              <span>{t('register.registering')}</span>
            </>
          ) : (
            t('register.registerButton')
          )}
        </button>
      </form>

      <div className="login-footer">
        <p className="login-footer-text">
          {t('register.hasAccount')}{" "}
          <button className="signup-btn" onClick={() => switchMode("login")} disabled={isLoading}>
            {t('register.login')}
          </button>
        </p>
      </div>
    </>
  );

  const renderForgotPasswordForm = () => (
    <>
      <div className="login-header">
        <div className="login-logo">
          <img src={logoImage} alt={t('logoAlt')} className="logo-image" />
        </div>
        <h1 className="login-title">{t('forgotPassword.title')}</h1>
        <p className="login-subtitle">{t('forgotPassword.enterEmailToReceiveCode')}</p>
      </div>

      <form className="login-form" onSubmit={handleForgotPasswordSubmit}>
        <div className="form-group">
          <label className="form-label">{t('forgotPassword.email')}</label>
          <input
            type="email"
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('forgotPassword.emailPlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        <button type="submit" className="login-btn" disabled={isLoading || !email}>
          {isLoading ? (
            <>
              <span className="spinner"></span>
              <span>{t('forgotPassword.sending')}</span>
            </>
          ) : (
            t('forgotPassword.sendCodeButton')
          )}
        </button>

        <div className="login-footer">
          <p className="login-footer-text">
            {t('forgotPassword.rememberPassword')}{" "}
            <button
              type="button"
              className="signup-btn"
              onClick={() => switchMode("login")}
              disabled={isLoading}
            >
              {t('forgotPassword.backToLogin')}
            </button>
          </p>
        </div>
      </form>
    </>
  );

  const renderResetPasswordForm = () => (
    <>
      <div className="login-header">
        <div className="login-logo">
          <img src={logoImage} alt={t('logoAlt')} className="logo-image" />
        </div>
        <h1 className="login-title">{t('resetPassword.title')}</h1>
        <p className="login-subtitle">{t('resetPassword.enterNewPassword')}</p>
        <p className="login-subtitle" style={{ fontWeight: "bold" }}>{pendingEmail}</p>
      </div>

      <form className="login-form" onSubmit={handleResetPasswordSubmit}>
        <div className="form-group">
          <label className="form-label">{t('resetPassword.newPassword')}</label>
          <input
            type="password"
            className="form-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('resetPassword.passwordPlaceholder')}
            required
            minLength={8}
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('resetPassword.confirmPassword')}</label>
          <input
            type="password"
            className="form-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            required
            minLength={8}
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          className="login-btn"
          disabled={isLoading || !newPassword || !confirmPassword}
        >
          {isLoading ? (
            <>
              <span className="spinner"></span>
              <span>{t('resetPassword.resetting')}</span>
            </>
          ) : (
            t('resetPassword.resetButton')
          )}
        </button>

        <div className="login-footer">
          <p className="login-footer-text">
            <button
              type="button"
              className="signup-btn"
              onClick={() => switchMode("login")}
              disabled={isLoading}
            >
              {t('resetPassword.backToLogin')}
            </button>
          </p>
        </div>
      </form>
    </>
  );

  return (
    <>
      <NotificationSystem notifications={notifications} onRemove={removeNotification} />

      <div className="login-container">
        <div className="login-card">
          {mode === "login" && renderLoginForm()}
          {mode === "register" && selfSignUpEnabled && renderRegisterForm()}
          {mode === "forgotPassword" && renderForgotPasswordForm()}
          {mode === "resetPassword" && renderResetPasswordForm()}
        </div>
      </div>
    </>
  );
};

export default Login;
